import { useEffect, useMemo, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SectionHeading } from "@/components/site/SectionHeading";
import { useSiteConfig, mailHref, telHref } from "@/lib/site-config";
import { useSeo } from "@/hooks/useSeo";
import { useActiveHeading } from "@/hooks/useActiveHeading";
import { cn } from "@/lib/utils";

/**
 * Long-form legal page template — used by `/privacy` and `/terms`.
 *
 * IMPORTANT: The copy on these pages is editorial boilerplate intended
 * to be reviewed and approved by the client's legal counsel before
 * launch. Hive Homes operates in the UAE and is subject to:
 *   - Federal Decree-Law No. 45 of 2021 on the Protection of Personal
 *     Data (the "PDPL") and its Executive Regulations;
 *   - Dubai Law No. 26 of 2007 (Regulating Real Estate Brokers in
 *     Dubai) and DLD/RERA bylaws; and
 *   - Federal Decree-Law No. 34 of 2021 on Combating Rumours and
 *     Cybercrimes.
 *
 * The structure mirrors the AboutPage / ArticlePage layout so it sits
 * neatly inside the brand. Sections are passed in as `children` so each
 * page reads naturally without re-implementing the chrome.
 */
export interface LegalSection {
  id?: string;
  heading: string;
  body: ReactNode;
}

interface LegalPageProps {
  eyebrow: string;
  title: string;
  intro: string;
  lastUpdated: string;
  sections: LegalSection[];
  seoTitle: string;
  seoDescription: string;
}

export function LegalPage({
  eyebrow,
  title,
  intro,
  lastUpdated,
  sections,
  seoTitle,
  seoDescription,
}: LegalPageProps) {
  useSeo({ title: seoTitle, description: seoDescription });

  /* Resolve every section's id once. The memoisation keeps the array
   * reference stable across renders so `useActiveHeading`'s effect
   * doesn't tear down its IntersectionObserver on every keystroke /
   * unrelated render. */
  const sectionIds = useMemo(
    () => sections.map((s) => s.id ?? slugify(s.heading)),
    [sections],
  );
  const activeId = useActiveHeading(sectionIds);

  // React Router doesn't scroll to `#fragment` targets by default —
  // when a TOC link is clicked the URL updates but the viewport stays
  // put. This effect handles both the initial page load (deep link with
  // a hash) and subsequent in-page TOC clicks, and gracefully falls back
  // to the page top if the fragment is missing.
  const { hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, "");
    if (!id) return;
    // Defer one frame so React has committed the section markup before
    // we try to scroll to it.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [hash]);

  const scrollToTop = () => {
    if (typeof window === "undefined") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Drop the `#fragment` from the URL so deep-link reload doesn't
    // immediately re-scroll back down to wherever we just left.
    if (window.location.hash) {
      navigate(window.location.pathname + window.location.search, {
        replace: true,
      });
    }
  };

  return (
    <>
      <section className="border-b border-[var(--line)] bg-[var(--bg-2)]">
        <div className="container-wide py-14 md:py-20 short:py-10">
          <SectionHeading
            as="h1"
            eyebrow={eyebrow}
            size="xl"
            title={title}
            body={intro}
          />
          <p className="mt-6 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)] sm:mt-8">
            Last updated · {lastUpdated}
          </p>
        </div>
      </section>

      <section className="container-wide grid gap-10 py-12 md:grid-cols-12 md:gap-14 md:py-16 short:py-8">
        {/* Table of contents — sticks alongside on desktop */}
        <aside className="md:col-span-3">
          <nav
            aria-label="On this page"
            className="md:sticky md:top-32 md:self-start"
          >
            <p className="eyebrow text-[var(--text-muted)]">On this page</p>
            <ol className="mt-4 flex flex-col gap-2.5 border-t border-[var(--line)] pt-4 text-[13px] text-[var(--text-soft)]">
              {sections.map((s, i) => {
                const id = sectionIds[i] ?? slugify(s.heading);
                const isActive = activeId === id;
                return (
                  <li key={id}>
                    <a
                      href={`#${id}`}
                      aria-current={isActive ? "location" : undefined}
                      className={cn(
                        // `relative` + `pl-3` reserves space for the
                        // active marker so the line never jumps width
                        // when highlighting changes.
                        "relative flex gap-3 pl-3 transition-colors hover:text-[var(--gold-bright)]",
                        isActive && "text-[var(--gold-bright)]",
                      )}
                    >
                      {/* Vertical gold bar pinned to the left of the
                          active entry — purely decorative, but it
                          provides a much stronger visual lock-in than
                          colour alone. The opacity transition keeps
                          the change from feeling abrupt. */}
                      <span
                        aria-hidden
                        className={cn(
                          "absolute left-0 top-1.5 h-3.5 w-0.5 bg-[var(--gold-bright)] transition-opacity duration-200",
                          isActive ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span
                        className={cn(
                          "tabular-nums transition-colors",
                          isActive
                            ? "text-[var(--gold-bright)]"
                            : "text-[var(--text-muted)]",
                        )}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span>{s.heading}</span>
                    </a>
                  </li>
                );
              })}
            </ol>
          </nav>
        </aside>

        {/* Body */}
        <article className="md:col-span-9 md:pl-2">
          <div className="flex flex-col gap-14">
            {sections.map((s, i) => {
              const id = sectionIds[i] ?? slugify(s.heading);
              return (
                <section
                  key={id}
                  id={id}
                  className="scroll-mt-32 first:pt-0 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-[var(--line)] [&:not(:first-child)]:pt-12"
                >
                  <p className="eyebrow text-[var(--text-muted)]">
                    Section {String(i + 1).padStart(2, "0")}
                  </p>
                  <h2
                    className="mt-3 font-display text-[24px] leading-[1.15] text-[var(--text)] sm:text-[28px] md:text-[34px]"
                    style={{ fontVariationSettings: '"opsz" 72' }}
                  >
                    {s.heading}
                  </h2>
                  <div className="legal-body mt-5 max-w-[68ch] text-[15px] leading-[1.75] text-[var(--text-soft)] sm:text-[15.5px]">
                    {s.body}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Back to top — a quiet text affordance after the last
              section. Using a button (not a `#top` anchor) means we
              control scroll behaviour and can clean up the URL hash
              so a deep-link reload doesn't immediately jump back. */}
          <div className="mt-14 flex justify-end border-t border-[var(--line)] pt-6">
            <button
              type="button"
              onClick={scrollToTop}
              className="group inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--text-muted)] transition-colors hover:text-[var(--gold-bright)]"
            >
              <span
                aria-hidden
                className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--line-strong)] transition-colors group-hover:border-[var(--gold-bright)] group-hover:text-[var(--gold-bright)]"
              >
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 9 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden
                >
                  <path
                    d="M4.5 8V1M4.5 1L1 4.5M4.5 1L8 4.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Back to top
            </button>
          </div>

          <ContactBlock />
        </article>
      </section>
    </>
  );
}

function ContactBlock() {
  const siteConfig = useSiteConfig();
  return (
    <aside className="mt-16 border-t border-[var(--line)] pt-10">
      <p className="eyebrow">Questions?</p>
      <h3
        className="mt-3 font-display text-[22px] leading-tight text-[var(--text)] sm:text-[26px]"
        style={{ fontVariationSettings: '"opsz" 72' }}
      >
        Speak to our team directly.
      </h3>
      <p className="mt-3 max-w-[60ch] text-[15px] leading-[1.7] text-[var(--text-soft)]">
        If anything on this page is unclear, or you would like to exercise any
        of the rights described above, our team will respond personally within
        two business days.
      </p>
      <ul className="mt-5 flex flex-col gap-1.5 text-[15px]">
        {siteConfig.email.set && (
          <li>
            <a
              href={mailHref(siteConfig.email)}
              className="link-arrow text-[var(--text)] hover:text-[var(--gold-bright)]"
            >
              {siteConfig.email.display}
            </a>
          </li>
        )}
        {siteConfig.phone.set && (
          <li>
            <a
              href={telHref(siteConfig.phone)}
              className="text-[var(--text)] hover:text-[var(--gold-bright)]"
            >
              {siteConfig.phone.display}
            </a>
          </li>
        )}
        <li>
          <Link
            to="/contact"
            className="link-arrow text-[var(--text)] hover:text-[var(--gold-bright)]"
          >
            Send us a message
          </Link>
        </li>
      </ul>
    </aside>
  );
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
